import React, { useEffect, useState, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { isBot } from "../lib/geo";
import { AhJinLogo } from "./AhJinLogo";
const FlowingRibbons = lazy(() => import("./FlowingRibbons"));

const LETTERS = ["A", "H", "-", "J", "I", "N"];
const PHRASES = [
  "Incorporation & licensing",
  "Platforms, liquidity & payments",
  "From idea to a live business",
];

const LogoSplash = () => {
  const [show, setShow] = useState(() => !isBot());
  const [phrase, setPhrase] = useState(0);
  const [pct, setPct] = useState(0);

  useEffect(() => {
    if (!show) return;
    const start = performance.now();
    const DURATION = 1500;
    const pi = setInterval(() => setPhrase((i) => Math.min(i + 1, PHRASES.length - 1)), 600);
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / DURATION);
      const eased = 1 - Math.pow(1 - p, 3);
      setPct(Math.round(eased * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const t = setTimeout(() => setShow(false), 1950);
    return () => { clearTimeout(t); clearInterval(pi); cancelAnimationFrame(raf); };
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="splash"
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            scale: 1.06,
            filter: "blur(22px)",
            transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
          }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#02050c] overflow-hidden"
          data-testid="logo-splash"
        >
          {/* flowing lines backdrop — same as solution pages */}
          <Suspense fallback={null}><FlowingRibbons className="absolute inset-0 pointer-events-none opacity-70" /></Suspense>

          {/* gold flash on exit */}
          <motion.div
            className="absolute inset-0 pointer-events-none bg-gradient-to-br from-[#FFE9A8]/50 via-transparent to-[#A855F7]/40"
            initial={{ opacity: 0 }}
            exit={{ opacity: [0, 0.8, 0], transition: { duration: 0.5, times: [0, 0.3, 1] } }}
          />

          <div className="relative flex flex-col items-center">
            {/* logo — calm, elegant entrance */}
            <motion.div
              initial={{ opacity: 0, y: 18, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <AhJinLogo size={216} animate={false} showWordmark={false} />
            </motion.div>

            {/* AH-JIN letters, staggered reveal */}
            <div className="mt-7 flex items-center gap-[0.35em]" data-testid="splash-wordmark" aria-label="AH-JIN">
              {LETTERS.map((ch, i) => (
                <motion.span
                  key={`${ch}-${i}`}
                  initial={{ opacity: 0, y: 22, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ delay: 0.55 + i * 0.09, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  className="font-display text-5xl sm:text-6xl tracking-[0.18em]"
                  style={{
                    background: "linear-gradient(120deg, #C4B5FD 0%, #F8F9FA 35%, #FFE9A8 65%, #E5C56F 100%)",
                    backgroundSize: "200% 100%",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    textShadow: "0 0 34px rgba(168,85,247,0.25)",
                    animation: "splashShimmer 2.2s ease-in-out infinite",
                  }}
                >
                  {ch}
                </motion.span>
              ))}
            </div>

            {/* rotating statement — what Ah-Jin does, readable while the page loads */}
            <div className="mt-6 h-8 sm:h-9 relative w-[90vw] max-w-xl text-center" data-testid="splash-phrase">
              <AnimatePresence mode="wait">
                <motion.div
                  key={phrase}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-x-0 font-display italic text-xl sm:text-2xl lg:text-3xl text-zinc-200 tracking-tight"
                >
                  {PHRASES[phrase]}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* loading progress line */}
            <div className="mt-5 w-56 h-[2px] bg-white/10 overflow-hidden rounded-full" data-testid="splash-progress">
              <div
                className="h-full bg-gradient-to-r from-purple-500 via-[#A855F7] to-[#E5C56F] rounded-full"
                style={{ width: `${pct}%`, transition: "width 0.1s linear", boxShadow: "0 0 12px rgba(168,85,247,0.7)" }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LogoSplash;
